import {Button, Card, CardActions, CardContent, Grid, MenuItem, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import * as Yup from "yup";
import dayjs from "dayjs";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useRoyaltiesPrintoutRepository} from "../../../api/repositories/RoyaltiesPrintoutRepository";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useRoyaltiesReportRepository} from "../../../api/repositories/RoyaltiesReportRepository";
import {getLiteralMonth, getLiteralMonths, getPastYears} from "../../../utils/FinanceDateUtils";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {FormSelect} from "../../../experiments/forms/FormSelect";
import {RoyaltyContractor} from "../constants/RoyaltiesModels";
import '../../../style/FinanceTable.css'
import MultiSelect from "../../../component/MultiSelect";
import CustomLoader from "../../../../../util/component/CustomLoader";

type PrintoutInputs = {
    year: number,
    month: number,
    legalEntity: string,
    dealer: string,
    contractors: Array<string>
}

const CreatePrintout = () => {

    const {t} = useTranslation("finance", {keyPrefix: 'royalties'});
    const {t: tg} = useTranslation("translation");

    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));


    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));


    const {postPrintoutRequest} = useRoyaltiesReportRepository();
    const {getContractors} = useRoyaltiesPrintoutRepository();

    const navigate = useNavigate();

    const [contractors, setContractors] = useState<RoyaltyContractor[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const schema = Yup.object().shape({
        year: Yup.number().required(),
        month: Yup.number().required(),
        legalEntity: Yup.string().required(),
        dealer: Yup.string().required(),
        contractors: Yup.array().min(1, t('printout-wizard.form.contractors-mandatory')),
    });

    const defaultValues: PrintoutInputs = {
        year: dayjs().month() > 0 ? dayjs().year() : dayjs().year() - 1,
        month: dayjs().month() > 0 ? dayjs().month() - 1 : 11,
        legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
        dealer: dealerOptions.length == 1 ? dealerOptions[0].value : "",
        contractors: [] as Array<string>,
    }

    const {handleSubmit, control, watch, setValue, clearErrors} = useForm<PrintoutInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });
    const watcher = watch();

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setValue('dealer', '');
            setDealers([]);
            setValue('contractors', []);
            setContractors([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        let legalEntity = watcher.legalEntity;
        let dealer = watcher.dealer;
        if (legalEntity == '' || dealer == '') {
            setValue('contractors', []);
            setContractors([]);
            return;
        }

        setValue('contractors', []);
        setContractors([]);

        getContractors(legalEntity, dealer).then((response) => {
            if (response.length > 0) {
                setContractors(response);
            }
        });

    }, [watcher.legalEntity, watcher.dealer]);

    const onSubmit: SubmitHandler<PrintoutInputs> = (data) => {

        let finalYear = data.year || 0;
        let finalMonth = getLiteralMonth(data.month);
        let finalContractors: any[] = contractors.filter(contractor => data.contractors.includes(contractor.msName + " - " + contractor.name))
        let printoutRequest: any = {
            year: finalYear,
            month: finalMonth,
            financeContractors: finalContractors,
            dealer: data.dealer,
            legalEntity: data.legalEntity
        };

        setIsLoading(true)
        postPrintoutRequest(printoutRequest)
            .then(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('printout-wizard.snackbar.success'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
                setIsLoading(false)
                navigate('/finance/royalties');
            })
            .catch(() => setIsLoading(false));
    }


    return (
        <Card variant="outlined" sx={{margin: '15px'}}>
            <form onSubmit={handleSubmit(data => onSubmit(data), errors => console.log(errors))} noValidate>
                <CardContent>
                    <Grid container spacing={2} alignItems={'flex-end'}>
                        <Grid item xs={12}>
                            <Typography variant={'body2'} color={'primary.light'} fontWeight={700}>
                                {t('printout-wizard.form.title')}
                            </Typography>
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={'year'}
                                control={control}
                                props={{
                                    required: true,
                                    variant: "standard",
                                    fullWidth: true,
                                    label: t('printout-wizard.form.year-label')
                                }}
                                items={getPastYears(10, dayjs().month() === 0 ? dayjs().year() - 1 : dayjs().year()).reverse().map((it: number) => (
                                    <MenuItem key={it} value={it}>
                                        {it}
                                    </MenuItem>
                                ))}/>

                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={'month'}
                                control={control}
                                props={{
                                    required: true,
                                    variant: "standard",
                                    fullWidth: true,
                                    label: t('printout-wizard.form.month-label')
                                }}
                                items={getLiteralMonths().map((it: string, index) => (
                                    <MenuItem key={it} value={index}
                                              disabled={watcher.year === dayjs().year() && index > dayjs().month()}>
                                        {it.charAt(0).toUpperCase() + it.slice(1)}
                                    </MenuItem>
                                ))}/>
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={"legalEntity"}
                                control={control}
                                props={{
                                    label: tg("shared.legalEntity"),
                                    fullWidth: true,
                                    required: true,
                                    onChange: (e: any) => {
                                        handleChangeLegalEntities(e.target.value)
                                    },
                                }}

                                items={legalEntityOptions.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={"dealer"}
                                control={control}
                                props={{
                                    label: tg("shared.dealer"),
                                    fullWidth: true,
                                    required: true,
                                    disabled: dealers.length == 0
                                }}
                                items={dealers.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={12} sm={12} xs={12}>
                            <Controller
                                control={control}
                                name="contractors"
                                render={({field, fieldState: {error}}) => (
                                    <MultiSelect items={contractors.map(c => c.msName + " - " + c.name)}
                                                 label={t("printout-wizard.form.dms-contractors")}
                                                 fieldName={"contractors"}
                                                 setValue={setValue}
                                                 selectAllLabel={t('printout-wizard.form.contractors-label-all')}
                                                 errorMessage={error?.message} clearErrors={clearErrors}
                                                 disabled={watcher.legalEntity === '' || watcher.dealer === ''}
                                    />
                                )}/>
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{justifyContent: 'flex-end'}}>
                    <Button variant={"outlined"} color={"accent"}
                            onClick={() => navigate(-1)}>{t('printout-wizard.buttons.cancel')}</Button>
                    <Button color={"accent"} type="submit">{t('printout-wizard.buttons.create')}</Button>
                </CardActions>
            </form>
            <CustomLoader loading={isLoading}/>
        </Card>
    )
}

export default CreatePrintout;